import React, { lazy } from "react";
import { Routes, Route } from "react-router-dom";
import Layout from "@/components/site/Layout";

// The Suspense boundary that waits for these chunks wraps <Outlet /> in Layout.jsx.
// Route-level code splitting (SEO audit: "pages load too slowly"). Only the shared
// shell (Layout + router + React) ships in the main bundle; every page — including
// the home page, whose estimator / coverage widgets are heavy — is its own chunk,
// fetched on first visit. Pages are prerendered to static HTML, so on a
// direct hit the visitor sees the full page immediately while the chunk loads —
// React 19 keeps the server-rendered markup in place for a suspended Suspense
// boundary during hydration instead of showing the fallback. The prerenderer
// (scripts/prerender-entry.jsx) uses renderToPipeableStream + onAllReady, which
// waits for the lazy modules, so the static HTML always contains the real page.
const Home = lazy(() => import("@/pages/Home"));
const ServicesHub = lazy(() => import("@/pages/ServicesHub"));
const ServiceDetail = lazy(() => import("@/pages/ServiceDetail"));
const LocationsHub = lazy(() => import("@/pages/LocationsHub"));
const LocationDetail = lazy(() => import("@/pages/LocationDetail"));
const About = lazy(() => import("@/pages/About"));
const ProjectsPage = lazy(() => import("@/pages/ProjectsPage"));
const ReviewsPage = lazy(() => import("@/pages/ReviewsPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const FinancingPage = lazy(() => import("@/pages/FinancingPage"));
const EstimatePage = lazy(() => import("@/pages/EstimatePage"));
const EstimatorRedirect = lazy(() => import("@/pages/EstimatorRedirect"));
const BidRequestPage = lazy(() => import("@/pages/BidRequestPage"));
const AdminPricing = lazy(() => import("@/pages/AdminPricing"));
const CityPage = lazy(() => import("@/pages/CityPage"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const BlogIndex = lazy(() => import("@/pages/BlogIndex"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const SiteMap = lazy(() => import("@/pages/SiteMap"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const NotFound = lazy(() => import("@/pages/NotFound"));

// Shared route tree. Used by:
//  - the browser app  (App.js  -> <BrowserRouter><AppRoutes/></BrowserRouter>)
//  - the prerenderer  (scripts/prerender-entry.jsx -> <StaticRouter location={path}><AppRoutes/></StaticRouter>)
// Keep this file free of browser-only code so it stays server-renderable.
export const AppRoutes = () => (
  <Routes>
    <Route element={<Layout />}>
      <Route path="/" element={<Home />} />
      <Route path="/services" element={<ServicesHub />} />
      <Route path="/services/:slug" element={<ServiceDetail />} />
      <Route path="/locations" element={<LocationsHub />} />
      <Route path="/locations/:slug" element={<LocationDetail />} />
      <Route path="/plumber/:slug" element={<CityPage />} />
      <Route path="/about" element={<About />} />
      <Route path="/projects" element={<ProjectsPage />} />
      <Route path="/reviews" element={<ReviewsPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/financing" element={<FinancingPage />} />
      <Route path="/estimate" element={<EstimatePage />} />
      {/* Feature 06: reopen a stored estimate by its 8-character ID (?book=1 jumps to /contact pre-filled). */}
      <Route path="/estimate/r/:id" element={<EstimatePage />} />
      <Route path="/estimate/bid-request" element={<BidRequestPage />} />
      {/* Legacy URL: client-side replace() to /estimate (no server redirect layer on this host). */}
      <Route path="/estimator" element={<EstimatorRedirect />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      {/* SEO audit batch 2, Part 5: plain-HTML site map (listed in sitemap.xml; footer "Sitemap" link). */}
      <Route path="/site-map" element={<SiteMap />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/admin/pricing" element={<AdminPricing />} />
      <Route path="*" element={<NotFound />} />
    </Route>
  </Routes>
);
